import React, { useCallback, useEffect, useRef, useState } from 'react';
import { theme, card, input, btnPrimary, btnSecondary } from '../theme';

/**
 * ShareEmailTemplateEditor — Settings → Emails.
 *
 * 2026-09-15 — Brett: "can we have a more professional send out email from
 * droppin." and his standing rule that every outgoing email's wording is his to
 * edit. Each email's subject, heading, message and button label are editable,
 * with tokens; the facts, the button's link and the expiry are always added by
 * the system, so an edit can never leave the recipient without the way in. The
 * preview is the real email, rendered by the server with sample details.
 *
 * Later the same day ("fix all the bugs") the subcontractor invite joined the
 * report share email here, so the card picks which email it is editing.
 */

const FIELDS = [
  { key: 'subject', label: 'Subject', max: 200 },
  { key: 'heading', label: 'Heading', max: 150 },
  { key: 'message', label: 'Message', max: 4000, rows: 6 },
  { key: 'buttonLabel', label: 'Button', max: 40 },
];

async function call(method, path, body) {
  const r = await fetch(path, {
    method,
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', 'X-DropPin-Fresh': '1' },
    body: body ? JSON.stringify(body) : undefined,
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
  return j;
}

export default function ShareEmailTemplateEditor() {
  const [templates, setTemplates] = useState([]);
  const [slug, setSlug] = useState('report.share');
  const [meta, setMeta] = useState(null);
  const [form, setForm] = useState(null);
  const [customised, setCustomised] = useState(false);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState(null);
  const refs = useRef({});
  const lastField = useRef('message');

  const apply = useCallback((j) => {
    setMeta(j);
    setForm({ ...j.template });
    setCustomised(!!j.customised);
  }, []);

  useEffect(() => {
    call('GET', '/api/email-templates?list=1')
      .then((j) => setTemplates(Array.isArray(j.templates) ? j.templates : []))
      .catch(() => setTemplates([]));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setForm(null); setPreview(null); setSavedAt(null); setError(null);
    call('GET', `/api/email-templates?slug=${encodeURIComponent(slug)}`)
      .then((j) => { if (!cancelled) apply(j); })
      .catch((e) => { if (!cancelled) setError(e.message || 'Could not load the email.'); });
    return () => { cancelled = true; };
  }, [slug, apply]);

  // The preview follows the fields as they are typed.
  useEffect(() => {
    if (!form) return undefined;
    const t = setTimeout(() => {
      call('POST', '/api/email-templates/preview', { slug, ...form })
        .then(setPreview)
        .catch((e) => setError(e.message || 'Could not render the preview.'));
    }, 400);
    return () => clearTimeout(t);
  }, [form, slug]);

  const setField = (key, value) => { setForm((f) => ({ ...f, [key]: value })); setSavedAt(null); };

  const insertToken = (token) => {
    const key = lastField.current;
    const el = refs.current[key];
    const text = `{{${token}}}`;
    const value = (form && form[key]) || '';
    const start = el && typeof el.selectionStart === 'number' ? el.selectionStart : value.length;
    const end = el && typeof el.selectionEnd === 'number' ? el.selectionEnd : value.length;
    setField(key, value.slice(0, start) + text + value.slice(end));
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      const pos = start + text.length;
      try { el.setSelectionRange(pos, pos); } catch (_) { /* not a text field */ }
    });
  };

  const save = async () => {
    setSaving(true); setError(null);
    try {
      apply(await call('PUT', '/api/email-templates', { slug, ...form }));
      setSavedAt(new Date());
    } catch (e) {
      setError(e.message || 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm('Put the default wording back?')) return;
    setSaving(true); setError(null);
    try {
      apply(await call('DELETE', `/api/email-templates?slug=${encodeURIComponent(slug)}`));
      setSavedAt(new Date());
    } catch (e) {
      setError(e.message || 'Could not reset.');
    } finally {
      setSaving(false);
    }
  };

  const tabs = templates.length ? templates : [{ slug: 'report.share', name: 'Report shared' }];

  return (
    <div style={{ ...card, padding: 18, maxWidth: 1100 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: theme.text }}>Emails</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {tabs.map((t) => (
            <button
              key={t.slug}
              type="button"
              onClick={() => setSlug(t.slug)}
              aria-pressed={slug === t.slug}
              style={{
                padding: '5px 12px', fontSize: 12, fontWeight: 600, borderRadius: 999, cursor: 'pointer', fontFamily: 'inherit',
                border: `1px solid ${slug === t.slug ? theme.brand : theme.border}`,
                background: slug === t.slug ? theme.brand : theme.surface,
                color: slug === t.slug ? '#fff' : theme.text,
              }}
            >{t.name}</button>
          ))}
        </div>
      </div>

      {!form ? (
        <div style={{ fontSize: 12, color: error ? theme.danger : theme.textLight }}>{error || 'Loading…'}</div>
      ) : (
        <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <div style={{ flex: '1 1 340px', minWidth: 0 }}>
            {FIELDS.map((f) => (
              <div key={f.key} style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: theme.text, marginBottom: 4 }}>{f.label}</label>
                {f.rows ? (
                  <textarea
                    ref={(el) => { refs.current[f.key] = el; }}
                    value={form[f.key] || ''}
                    maxLength={f.max}
                    rows={f.rows}
                    onFocus={() => { lastField.current = f.key; }}
                    onChange={(e) => setField(f.key, e.target.value)}
                    style={{ ...input, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }}
                  />
                ) : (
                  <input
                    ref={(el) => { refs.current[f.key] = el; }}
                    value={form[f.key] || ''}
                    maxLength={f.max}
                    onFocus={() => { lastField.current = f.key; }}
                    onChange={(e) => setField(f.key, e.target.value)}
                    style={input}
                  />
                )}
              </div>
            ))}

            <div style={{ fontSize: 12, fontWeight: 700, color: theme.text, marginBottom: 6 }}>Insert</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
              {((meta && meta.tokens) || []).map((t) => (
                <button
                  key={t.token}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => insertToken(t.token)}
                  title={`{{${t.token}}}`}
                  style={{
                    padding: '4px 10px', fontSize: 12, borderRadius: 999, cursor: 'pointer',
                    border: `1px solid ${theme.border}`, background: theme.surface, color: theme.text, fontFamily: 'inherit',
                  }}
                >{t.label}</button>
              ))}
            </div>

            {error && <div style={{ fontSize: 12, color: theme.danger, marginBottom: 10 }}>{error}</div>}
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <button type="button" onClick={save} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.5 : 1 }}>
                {saving ? 'Saving…' : 'Save email'}
              </button>
              {customised && (
                <button type="button" onClick={reset} disabled={saving} style={btnSecondary}>Use default wording</button>
              )}
              {savedAt && <span style={{ fontSize: 12, color: theme.textLight }}>Saved {savedAt.toLocaleTimeString()}.</span>}
            </div>
          </div>

          <div style={{ flex: '1 1 460px', minWidth: 0 }}>
            <div style={{ fontSize: 12, color: theme.textLight, marginBottom: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              <strong style={{ color: theme.text }}>Subject:</strong> {preview ? preview.subject : '…'}
            </div>
            <iframe
              title="Email preview"
              sandbox=""
              srcDoc={preview ? preview.html : ''}
              style={{ width: '100%', height: 760, border: `1px solid ${theme.border}`, borderRadius: 8, background: '#f3f4f6' }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
